// Azure connection by Lighthouse delegation (CCT-327).
//
// Replaces `CloudSpConfig`, where the player typed a subscription, a resource group, a client, a
// tenant and a SECRET, for every mission. Here they click once, deploy a template in their own
// subscription, and CloudChrono holds nothing of theirs but the Reader role.
//
// ⚠️ THE DETECTION TRIGGER LIVES HERE, AND NOWHERE ELSE. The backend runs no background sweep:
// this screen is the only party that knows the player went to the portal, so it asks on return
// (window focus) and on a decaying schedule, within a bounded window. Past that window it stops
// and hands back a retry button rather than polling forever.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Linking, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ApiError, apiFetch } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { getThemeColors } from '../utils/themeStyles';
import { onForeground } from '../utils/onForeground';

// Bounded verification window. A player who never finishes the deployment must not leave a tab
// asking Azure every few seconds until the end of time.
const FENETRE_MS = 5 * 60 * 1000;
const PREMIER_DELAI_MS = 4000;
const DELAI_MAX_MS = 30000;

type Etat = 'NONE' | 'PENDING' | 'VERIFYING' | 'READY' | 'EXPIRED' | 'REVOKED' | 'FAILED';

export default function AzureConnection({ missionId }: { missionId?: number }) {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const c = getThemeColors(isDarkTheme);
  const { getAccessToken } = useAuth();

  const [etat, setEtat] = useState<Etat>('NONE');
  const [requis, setRequis] = useState<boolean | null>(null);
  const [connectionId, setConnectionId] = useState('');
  const [souscription, setSouscription] = useState('');
  const [message, setMessage] = useState('');
  const [lien, setLien] = useState('');
  const [armKo, setArmKo] = useState(false);
  const [sansFormulaire, setSansFormulaire] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [surveille, setSurveille] = useState(false);

  const minuteur = useRef<any>(null);
  const finFenetre = useRef(0);
  const delai = useRef(PREMIER_DELAI_MS);

  // ⚠️ ON PASSE PAR LE CLIENT CENTRAL, PAS PAR `fetch`. Mesuré en session réelle : un onglet
  // laissé ouvert le temps d'un aller-retour dans le portail Azure présente un jeton périmé, et
  // l'écran affichait « invalid_token: Signature has expired » au clic sur « Connecter Azure ».
  // C'est le pire moment possible pour ce défaut : ce parcours EXIGE de quitter l'application
  // plusieurs minutes. `apiFetch` renouvelle en silence sur 401 et rejoue une fois.
  const appel = <T,>(chemin: string, opts: { method?: string; fond?: boolean } = {}) =>
    apiFetch<T>(chemin, {
      method: opts.method, token: getAccessToken(), auth: true, background: opts.fond === true,
    });

  const detail = (e: unknown): string => {
    if (e instanceof ApiError) {
      const corps: any = e.body;
      return String(corps?.detail ?? corps?.message ?? `HTTP ${e.status}`);
    }
    return String((e as any)?.message ?? e);
  };

  const arreterSurveillance = useCallback(() => {
    if (minuteur.current) { clearTimeout(minuteur.current); minuteur.current = null; }
    setSurveille(false);
  }, []);

  // --- State of the connection, plus whether this mission needs one at all ---------------
  const charger = useCallback(async () => {
    try {
      const q = missionId != null ? `?missionId=${missionId}` : '';
      const d = await appel<any>(`/api/azure/connections${q}`, { fond: true });
      setEtat((d.status || 'NONE') as Etat);
      setConnectionId(d.connectionId || '');
      setSouscription(d.subscriptionId || '');
      if (typeof d.required === 'boolean') setRequis(d.required);
    } catch { /* network: the panel simply says nothing rather than lying */ }
  }, [missionId]);

  useEffect(() => { void charger(); return arreterSurveillance; }, [charger, arreterSurveillance]);

  // --- Verification, triggered by the browser -------------------------------------------
  const verifier = useCallback(async (id: string): Promise<Etat> => {
    try {
      // Requête de FOND : la sonde ne doit pas allumer l'indicateur de chargement global à
      // chaque tour, sinon l'écran clignote pendant les cinq minutes de la fenêtre.
      const d = await appel<any>(`/api/azure/connections/${encodeURIComponent(id)}`, { fond: true });
      const s = (d.status || 'PENDING') as Etat;
      setEtat(s);
      if (d.subscriptionId) setSouscription(d.subscriptionId);
      if (d.message) setMessage(d.message);
      return s;
    } catch {
      return 'PENDING';
    }
  }, []);

  const planifier = useCallback((id: string) => {
    if (minuteur.current) clearTimeout(minuteur.current);
    minuteur.current = setTimeout(async () => {
      const s = await verifier(id);
      if (s === 'READY' || s === 'FAILED' || s === 'EXPIRED') { arreterSurveillance(); return; }
      if (Date.now() >= finFenetre.current) {
        // ⚠️ RENDRE LA MAIN, PAS SONDER SANS FIN. Une vérification qui n'aboutit pas doit se
        // solder par un bouton, sinon l'écran promet un travail de fond qui n'existe pas.
        arreterSurveillance();
        setMessage(t('azure.notSeenYet'));
        return;
      }
      // Decaying cadence: the deployment usually lands in the first seconds, and a player who
      // walked away should not cost one ARM scan every four seconds for five minutes.
      delai.current = Math.min(Math.round(delai.current * 1.6), DELAI_MAX_MS);
      planifier(id);
    }, delai.current);
  }, [verifier, arreterSurveillance, t]);

  const surveiller = useCallback((id: string) => {
    finFenetre.current = Date.now() + FENETRE_MS;
    delai.current = PREMIER_DELAI_MS;
    setSurveille(true);
    planifier(id);
  }, [planifier]);

  // ⚠️ LE VRAI SIGNAL, C'EST LE RETOUR DU JOUEUR. Il quitte l'onglet pour le portail Azure et
  // revient : ce moment précis vaut mieux que n'importe quelle minuterie, et il est gratuit.
  useEffect(() => {
    if (!surveille || !connectionId) return;
    const auRetour = () => {
      delai.current = PREMIER_DELAI_MS;
      void verifier(connectionId).then((s) => {
        if (s === 'READY' || s === 'FAILED' || s === 'EXPIRED') arreterSurveillance();
      });
    };
    // ⚠️ THE SAME DEFECT AS IN AuthContext — CCT-476. `typeof window === 'undefined'` does NOT
    // test for the web: React Native defines `window`, so the guard passed and the listener blew
    // up. This component is not on every path, so the crash was simply waiting its turn here.
    return onForeground(auRetour);
  }, [surveille, connectionId, verifier, arreterSurveillance]);

  // --- Actions ---------------------------------------------------------------------------
  const connecter = async () => {
    setOccupe(true); setMessage('');
    try {
      const d = await appel<any>('/api/azure/connections', { method: 'POST' });
      setConnectionId(d.connectionId || '');
      setEtat((d.status || 'PENDING') as Etat);
      // ⚠️ LE LIEN RESTE AFFICHÉ, IL N'EST PAS SEULEMENT OUVERT. `openURL` part d'un gestionnaire
      // asynchrone : un bloqueur de fenêtres surgissantes peut l'avaler sans rien dire, et le
      // joueur se retrouve devant un écran qui affirme avoir ouvert le portail. Le lien visible
      // est le chemin de secours, et il se copie.
      // ⚠️ THE PORTAL IS NO LONGER OPENED FROM HERE. Two reasons, the second one measured:
      //   * the portal ALWAYS titles the blade "Custom deployment" and shows the ARM icon — so
      //     the first thing a player sees, when asked for access on THEIR OWN subscription, is a
      //     generic infrastructure screen saying nothing about who is asking;
      //   * `openURL` called from an ASYNC handler is swallowed by a pop-up blocker without a
      //     word. Opened from the click on the panel below, it starts from a direct user gesture
      //     — which browsers let through.
      if (d.deployUrl) setLien(d.deployUrl);
      // ⚠️ ARM READS THE TEMPLATE SERVER-SIDE, the portal reads it in the browser. On a local
      // API the deployment therefore fails at the VERY LAST click, in Azure's own words
      // ("InvalidContentLink"), which say nothing about our configuration. Said here instead.
      setArmKo(d.armCanFetch === false);
      // ⚠️ THE BLOB PATH LOSES THE PRE-FILLED FORM, so it must carry its guidance. Measured at the
      // portal: "Validation failed. Required information is missing or not valid" with `Region: -`.
      // A subscription-scope deployment requires a location, and the plain custom-deployment blade
      // does not pick one — the named blade did. Saying it beats letting the player hunt through a
      // page of Marketplace terms for a field they never saw.
      setSansFormulaire(d.servedFrom === 'blob');
      if (d.reused) setMessage(t('azure.reuseExisting'));
      if (d.connectionId && d.status !== 'READY') surveiller(d.connectionId);
    } catch (e: unknown) {
      setMessage(detail(e));
    } finally {
      setOccupe(false);
    }
  };

  const deconnecter = async () => {
    if (!connectionId) return;
    setOccupe(true);
    try {
      const d = await appel<any>(`/api/azure/connections/${encodeURIComponent(connectionId)}`,
        { method: 'DELETE' });
      setEtat('REVOKED');
      setSouscription('');
      setMessage(d.azureCleanup || '');
    } catch (e: unknown) {
      setMessage(detail(e));
    } finally {
      setOccupe(false);
    }
  };

  /** Abandonner une connexion EN COURS, et repartir d'un écran propre.
   *
   * Distinct de la déconnexion d'une souscription reliée : ici rien n'est encore rattaché, il
   * n'y a donc rien à retirer côté Azure — et surtout rien à expliquer au joueur à ce sujet.
   * Ce qu'il veut, c'est un lien neuf, tout de suite.
   */
  const annuler = async () => {
    if (!connectionId) return;
    arreterSurveillance();
    setOccupe(true);
    try {
      await appel<any>(`/api/azure/connections/${encodeURIComponent(connectionId)}`,
        { method: 'DELETE' });
    } catch (e: unknown) {
      setMessage(detail(e));
    } finally {
      setConnectionId(''); setLien(''); setEtat('NONE'); setMessage(''); setOccupe(false);
    }
  };

  // Asked about a mission that needs nothing from Azure: stay out of the way.
  if (requis === false && etat !== 'READY') return null;

  const bouton = (libelle: string, onPress: () => void, principal = true) => (
    <TouchableOpacity onPress={onPress} disabled={occupe}
      style={[s.btn, {
        backgroundColor: principal ? c.primary : 'transparent',
        borderWidth: principal ? 0 : 1, borderColor: c.border, opacity: occupe ? 0.6 : 1,
      }]}>
      {occupe ? <ActivityIndicator color={principal ? '#fff' : c.textPrimary} />
        : <Text style={[s.btnText, { color: principal ? '#fff' : c.textPrimary }]}>{libelle}</Text>}
    </TouchableOpacity>
  );

  return (
    <View style={[s.card, { backgroundColor: c.cardBackground, borderColor: c.border }]}>
      <Text style={[s.title, { color: c.textPrimary }]}>{t('azure.title')}</Text>

      {etat === 'READY' ? (
        <>
          <Text style={[s.help, { color: c.textTertiary }]}>
            {t('azure.connectedHelp')}
          </Text>
          <Text style={[s.mono, { color: c.textSecondary }]}>{souscription}</Text>
          {bouton(t('azure.disconnect'), deconnecter, false)}
        </>
      ) : (
        <>
          <Text style={[s.help, { color: c.textTertiary }]}>{t('azure.help')}</Text>
          {!!lien && (
            // ⚠️ OUR IDENTITY COMES BEFORE THE REDIRECTION, NOT AFTER. The Azure portal always
            // titles the blade "Custom deployment" — NOT settable from a form: the title and the
            // icon belong to a Marketplace offer. A player asked for access on THEIR OWN
            // subscription would therefore meet an anonymous infrastructure screen first. This
            // panel carries the request; the portal becomes the confirmation.
            <View style={[s.identite, { borderColor: c.primary, backgroundColor: c.cardBackground }]}>
              <View style={s.marque}>
                <View style={[s.sceau, { borderColor: c.primary }]}>
                  <Text style={[s.sceauTexte, { color: c.primary }]}>C²</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[s.marqueNom, { color: c.textPrimary }]}>CloudChrono</Text>
                  <Text style={[s.help, { color: c.textTertiary }]}>{t('azure.identityLead')}</Text>
                </View>
              </View>
              <Text style={[s.help, { color: c.textSecondary }]}>{t('azure.identityScope')}</Text>
              <Text style={[s.help, { color: c.textSecondary }]}>{t('azure.identityRevoke')}</Text>
              {armKo && (
                <Text style={[s.help, { color: c.warning }]}>{t('azure.armCannotFetch')}</Text>
              )}
              {sansFormulaire && !armKo && (
                <Text style={[s.help, { color: c.warning }]}>{t('azure.pickRegion')}</Text>
              )}
              {bouton(t('azure.identityOpen'), () => { void Linking.openURL(lien); }, true)}
              {/* The raw link stays VISIBLE: a pop-up blocker can still swallow the opening, and
                  the player must be able to copy it rather than face a screen claiming the portal
                  was opened. */}
              <Text style={[s.label, { color: c.textSecondary }]}>{t('azure.linkLabel')}</Text>
              <Text selectable onPress={() => void Linking.openURL(lien)}
                style={[s.lien, { color: c.primary, borderColor: c.border }]}>{lien}</Text>
            </View>
          )}
          {surveille && (
            <View style={s.ligne}>
              <ActivityIndicator color={c.primary} />
              <Text style={[s.help, { color: c.textSecondary }]}>{t('azure.checking')}</Text>
            </View>
          )}
          {bouton(surveille || etat === 'PENDING' ? t('azure.retry') : t('azure.connect'),
            connectionId && !surveille && etat === 'PENDING'
              ? () => surveiller(connectionId)
              : connecter)}
          {/* ⚠️ ON DOIT POUVOIR ABANDONNER UNE CONNEXION EN COURS. Le bouton de déconnexion
              n'existait que sur `READY` : un joueur en `PENDING` — celui qui veut recommencer
              avec un autre compte Azure, exactement le cas rencontré — n'avait AUCUNE sortie et
              devait attendre l'expiration. La création réutilise la connexion vivante, donc
              recliquer ne rendait que le même lien mort. */}
          {!!connectionId && bouton(t('azure.cancel'), annuler, false)}
        </>
      )}

      {!!message && <Text style={[s.help, { color: c.textSecondary, marginTop: 10 }]}>{message}</Text>}
    </View>
  );
}

const s = StyleSheet.create({
  identite: { marginTop: 12, padding: 14, borderRadius: 12, borderWidth: 1, gap: 8 },
  marque: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  sceau: { width: 38, height: 38, borderRadius: 8, borderWidth: 2,
           alignItems: 'center', justifyContent: 'center' },
  sceauTexte: { fontSize: 17, fontWeight: '800', letterSpacing: -0.5 },
  marqueNom: { fontSize: 15, fontWeight: '700' },
  card: { borderRadius: 12, borderWidth: 1, padding: 16, marginVertical: 12 },
  title: { fontSize: 16, fontWeight: '700', marginBottom: 6 },
  help: { fontSize: 12, lineHeight: 17 },
  label: { fontSize: 11, fontWeight: '600' },
  lien: { fontSize: 11, lineHeight: 16, borderWidth: 1, borderRadius: 8, padding: 8 },
  mono: { fontSize: 12, marginTop: 8, fontFamily: 'monospace' },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 10 },
  btn: { marginTop: 14, borderRadius: 8, paddingVertical: 11, alignItems: 'center' },
  btnText: { fontWeight: '600', fontSize: 14 },
});
